import { useEffect, useRef, useMemo } from "react";
import styles from "./shopcategories.module.css";
import { IoFilterSharp } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";
import { useInfiniteProductsQuery } from "../../../queries/useProductsQuery";
import { Banner, BreadCrumb } from "../../shared/index";
import { ShopProducts, ShopFilters, SortFilter } from "../index";
import useFilters from "../../../hooks/useFilters";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { useGetWishLists } from "../../../queries/useWishListQuery";
import ProductPopup from "../PopupProduct/PopupProduct";

const formatLabel = (slug = "") =>
  slug.replace(/[_-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const ShopCategories = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("search");  
  const categoryFromUrl = searchParams.get("category");
  const subCategoryFromUrl = searchParams.get("subcategory");
  const childCategoryFromUrl = searchParams.get("childcategory");
  const prevCategoryRef = useRef(categoryFromUrl); 
  const {
  data: wishItems = [],
  isPending: isWishListPending,
} = useGetWishLists();
  /**
   * URL IS SOURCE OF TRUTH
   */
  const { filtersProps, filtersActions, queryParams } = useFilters({
    initialSearch: searchQuery,
    initialCategory: categoryFromUrl,
    initialSubCategory: subCategoryFromUrl,
  });
  const { sortBy } = filtersProps;
  const { setSortBy, setMobileFiltersOpen } = filtersActions;
  /**
   * FORCE API PARAMS TO FOLLOW URL
   */
 const finalQueryParams = useMemo(() => {
  return {
    ...queryParams,

    ...(categoryFromUrl && {
      category: categoryFromUrl,
    }),

    ...(subCategoryFromUrl && {
      subcategory: subCategoryFromUrl,
    }),
  };
}, [queryParams, categoryFromUrl, subCategoryFromUrl]);

  /**
   * API CALL
   */
 const {
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
  isError,
  refetch,
} = useInfiniteProductsQuery(finalQueryParams, {
  keepPreviousData: true,
  enabled: !!queryParams, // ✅ ADD THIS LINE
});
  
const brands = useMemo(() => {
  return data?.pages?.[0]?.filter_info?.brands || [];
}, [data]);
  /**      
   * PRODUCTS (NO FRONTEND FILTERING)
   */
 const products = useMemo(() => {
  const allProducts =
    data?.pages?.flatMap((p) => p?.products?.data ?? []) ?? [];
  // ✅ REMOVE OUT OF STOCK PRODUCTS
  return allProducts.filter((product) => product.qty > 0);
}, [data]); 

const categoriesRef = useRef([]);

const categories = useMemo(() => {
  const newCategories = data?.pages?.[0]?.filter_info?.categories;

  // ✅ If API returns valid categories → update ref
  if (newCategories && newCategories.length > 0) {
    categoriesRef.current = newCategories;
  }

  // ✅ Always return last valid categories
  return categoriesRef.current;
}, [data]);

const currentCategoryDescription = useMemo(() => {
  if (!categoryFromUrl || !categories.length) return "";

  const currentCategory = categories.find(
    (cat) => cat.slug === categoryFromUrl 
  );

  return currentCategory?.description || "";
}, [categories, categoryFromUrl]); 

const currentSubCategoryDescription = useMemo(() => {
  if (!subCategoryFromUrl || !categories.length) return "";

  for (const cat of categories) {  
    const sub = cat.subcategories?.find(  
      (s) => s.slug === subCategoryFromUrl
    );
    if (sub) return sub.description || "";
  }

  return "";
}, [categories, subCategoryFromUrl]);
  /**
   * 🔥 AUTO-FIX WRONG CATEGORY FROM SUBCATEGORY
   */
  useEffect(() => {
    if (!subCategoryFromUrl || !categories.length) return;
    const parentCategory = categories.find((cat) =>
      cat.subcategories?.some((sub) => sub.slug === subCategoryFromUrl)
    );
    if (parentCategory && parentCategory.slug !== categoryFromUrl) {
      const params = new URLSearchParams(location.search);
      params.set("category", parentCategory.slug);
      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
  }, [
    subCategoryFromUrl,
    categoryFromUrl,
    categories,
    location.pathname,
    location.search,
    navigate,
  ]);
  /**
   * PRICE RANGE
   */
  const { minPrice, maxPrice } = useMemo(() => {
    const mins = [];
    const maxs = [];
    data?.pages?.forEach((page) => {
      if (page?.filter_info?.price_min != null) {
        mins.push(Number(page.filter_info.price_min));
      }
      if (page?.filter_info?.price_max != null) {
        maxs.push(Number(page.filter_info.price_max));
      }
    });
    return {
      minPrice: mins.length ? Math.min(...mins) : 0,
      maxPrice: maxs.length ? Math.max(...maxs) : 10000,
    };
  }, [data]);
  /**
   * REMOVE SUBCATEGORY WHEN CATEGORY CHANGES
   */
  useEffect(() => {
    if (
      prevCategoryRef.current &&
      prevCategoryRef.current !== categoryFromUrl
    ) {
      const params = new URLSearchParams(location.search);
      params.delete("subcategory");
      navigate(
        `${location.pathname}?${params.toString()}`,
        { replace: true }
      );
    }
    prevCategoryRef.current = categoryFromUrl;
  }, [categoryFromUrl, location.pathname, location.search, navigate]);
  /**
   * REFRESH DATA ON URL CHANGE
   */
  // useEffect(() => {
  //   refetch();
  // }, [categoryFromUrl, subCategoryFromUrl, refetch]);
  const lastItemRef = useInfiniteScroll(
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  );  
  useEffect(() => {
  if ("scrollRestoration" in window.history) {
   
  }
  window.scrollTo({ top: 0, behavior: "auto" });
}, []);
  
  return (
    <section className={styles.container}>
      <BreadcrumbBanner
        heading={"Healthy vegetable that you deserve to eat fresh"}
        imageUrl="https://t3.ftcdn.net/jpg/06/04/15/56/240_F_604155665_HsqwLJzfEw88kjUqGkeFDpXXWKv5zSUc.jpg"
extraCrumbs={[
  categoryFromUrl && {
    label: formatLabel(categoryFromUrl),
    link: `/shop?category=${categoryFromUrl}`,
  },
  subCategoryFromUrl && {
    label: formatLabel(subCategoryFromUrl),
    link: `/shop?category=${categoryFromUrl}&subcategory=${subCategoryFromUrl}`,
  },
  childCategoryFromUrl && {
    label: formatLabel(childCategoryFromUrl),
    link: `/shop?category=${categoryFromUrl}&subcategory=${subCategoryFromUrl}&childcategory=${childCategoryFromUrl}`,
  },
].filter(Boolean)}
      />
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          <div className={styles.sidebarWrapper}>
            <ShopFilters
              minPrice={minPrice}
              maxPrice={maxPrice}
              categories={categories} 
               brands={brands} 
              //  colors={colors} 
              filtersProps={filtersProps}
              filtersActions={filtersActions}
            />
          </div>
          <div className={styles.rightContent}>
            <div className={styles.sortSection}>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className={styles.mobileFilterButton}
              >
                <IoFilterSharp /> Filters
              </button>
              <div className={styles.sortRight}>
                <SortFilter
                  heading="Sort By :"
                  sortBy={sortBy}
                  setSortBy={setSortBy}
                />
              </div>
            </div>  

            <ShopProducts 
              products={products}
              isPending={isPending}
              isError={isError}
              hasMore={hasNextPage}
              isFetching={isFetchingNextPage}
              lastItemRef={lastItemRef}
                wishItems={wishItems}
  isWishListPending={isWishListPending}
            /> 
            {(currentSubCategoryDescription || currentCategoryDescription) && (
  <div
    className={styles.categoryDescription}
    dangerouslySetInnerHTML={{
      __html: currentSubCategoryDescription || currentCategoryDescription,
    }}
  />
)}
          </div>
        </div>
      </div> 
        {/* <ProductPopup products={products} />  */}
    </section>
  );
};
const BreadcrumbBanner = ({ heading, imageUrl, extraCrumbs }) => {
  const breadcrumbPaths = [
    { label: "Shop", path: "/shop" },
    ...extraCrumbs.map((c) => ({
      label: c.label,
      path: c.link,
    })),
  ];
  return (
    <>
      <BreadCrumb customPaths={breadcrumbPaths} />
      <Banner heading={heading} imageUrl={imageUrl} />
    </>
  );
};
export default ShopCategories;